import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, X, Pencil } from 'lucide-react';
import { toast } from '../../hooks/useToast';
import { getErrorMessage } from '../../lib/api';
import { goalService } from '../../services/goal.service';
import { divisionService } from '../../services/division.service';
import type { Goal, GoalInput, Division } from '../../types/crm';
import type { PaginationMeta } from '../../types';
import Loading from '../../components/Loading';

const goalSchema = z.object({
  division_id: z.number().int().positive().nullable().optional(),
  year: z.number().int().min(1901, 'Year is required').max(2200),
  revenue_target: z.string().min(1, 'Revenue target is required'),
  cases_target: z.string().optional(),
  new_operators_target: z.string().optional(),
  notes: z.string().optional(),
});

type GoalFormData = z.infer<typeof goalSchema>;

const DEFAULT_FORM: GoalFormData = {
  division_id: null,
  year: new Date().getFullYear(),
  revenue_target: '',
  cases_target: '',
  new_operators_target: '',
  notes: '',
};

const toInput = (data: GoalFormData): GoalInput => ({
  division_id: data.division_id ?? null,
  year: data.year,
  revenue_target: data.revenue_target,
  cases_target: data.cases_target?.trim() ? data.cases_target.trim() : null,
  new_operators_target:
    data.new_operators_target?.trim() ? Number(data.new_operators_target) : null,
  notes: data.notes?.trim() ? data.notes.trim() : null,
});

export const Goals = () => {
  const [items, setItems] = useState<Goal[]>([]);
  const [, setMeta] = useState<PaginationMeta | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [divisions, setDivisions] = useState<Division[]>([]);
  const [yearFrom, setYearFrom] = useState<string>('');
  const [yearTo, setYearTo] = useState<string>('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Goal | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<GoalFormData>({
    resolver: zodResolver(goalSchema),
    defaultValues: DEFAULT_FORM,
  });

  const loadGoals = useCallback(async () => {
    setIsLoading(true);
    try {
      const q: Record<string, unknown> = {};
      if (yearFrom) q['year_gteq'] = Number(yearFrom);
      if (yearTo) q['year_lteq'] = Number(yearTo);

      const result = await goalService.getAll({ q });
      setItems(result.data);
      setMeta(result.meta);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, [yearFrom, yearTo]);

  const loadDivisions = useCallback(async () => {
    try {
      const result = await divisionService.getAll({ per_page: 200 });
      setDivisions(result.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  }, []);

  useEffect(() => {
    loadGoals();
  }, [loadGoals]);

  useEffect(() => {
    loadDivisions();
  }, [loadDivisions]);

  const openCreate = () => {
    setEditing(null);
    reset(DEFAULT_FORM);
    setShowModal(true);
  };

  const openEdit = (goal: Goal) => {
    setEditing(goal);
    reset({
      division_id: goal.division?.id ?? null,
      year: goal.year,
      revenue_target: goal.revenue_target,
      cases_target: goal.cases_target ?? '',
      new_operators_target:
        goal.new_operators_target != null ? String(goal.new_operators_target) : '',
      notes: goal.notes ?? '',
    });
    setShowModal(true);
  };

  const onSubmit = async (data: GoalFormData) => {
    setIsSaving(true);
    try {
      const payload = toInput(data);
      if (editing) {
        await goalService.update(editing.id, payload);
        toast.success('Goal updated');
      } else {
        await goalService.create(payload);
        toast.success('Goal created');
      }
      setShowModal(false);
      reset(DEFAULT_FORM);
      loadGoals();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (goal: Goal) => {
    const label = goal.division?.name ?? 'account-wide';
    if (!confirm(`Delete ${goal.year} ${label} goal?`)) return;
    try {
      await goalService.delete(goal.id);
      toast.success('Goal deleted');
      loadGoals();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Goals</h1>
          <p className="text-sm text-gray-600 mt-1">
            Annual CRM goals, account-wide or per division
          </p>
        </div>
        <button onClick={openCreate} className="btn btn-primary">
          <Plus className="h-4 w-4 inline mr-2" />
          Add Goal
        </button>
      </div>

      {/* Filters */}
      <div className="card grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="label">Year from</label>
          <input
            type="number"
            className="input"
            value={yearFrom}
            onChange={(e) => setYearFrom(e.target.value)}
            placeholder="e.g. 2026"
          />
        </div>
        <div>
          <label className="label">Year to</label>
          <input
            type="number"
            className="input"
            value={yearTo}
            onChange={(e) => setYearTo(e.target.value)}
            placeholder="e.g. 2028"
          />
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <Loading />
      ) : items.length === 0 ? (
        <div className="card text-center text-gray-500 py-12">No goals found.</div>
      ) : (
        <div className="card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Year</th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Division</th>
                  <th className="px-6 py-3 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue</th>
                  <th className="px-6 py-3 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Cases</th>
                  <th className="px-6 py-3 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">New Operators</th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes</th>
                  <th className="px-6 py-3 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {items.map((goal) => (
                  <tr key={goal.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {goal.year}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {goal.division?.name ?? 'Account-wide'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 text-right">
                      ${goal.revenue_target}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 text-right">
                      {goal.cases_target ?? '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 text-right">
                      {goal.new_operators_target ?? '—'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                      {goal.notes ?? '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-3">
                      <button
                        onClick={() => openEdit(goal)}
                        className="text-blue-600 hover:text-blue-900"
                        aria-label={`Edit ${goal.year} goal`}
                      >
                        <Pencil className="h-4 w-4 inline" />
                      </button>
                      <button
                        onClick={() => handleDelete(goal)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div
              className="fixed inset-0 transition-opacity bg-(--color-bg-scrim)"
              onClick={() => setShowModal(false)}
            />
            <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {editing ? 'Edit Goal' : 'Add Goal'}
                    </h3>
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="year" className="label">Year</label>
                        <input
                          {...register('year', { valueAsNumber: true })}
                          id="year"
                          type="number"
                          className="input"
                        />
                        {errors.year && (
                          <p className="mt-1 text-sm text-red-600">{errors.year.message}</p>
                        )}
                      </div>
                      <div>
                        <label htmlFor="division_id" className="label">Division (optional)</label>
                        <select
                          {...register('division_id', {
                            setValueAs: (v) => (v === '' || v == null ? null : Number(v)),
                          })}
                          id="division_id"
                          className="input"
                        >
                          <option value="">Account-wide</option>
                          {divisions.map((d) => (
                            <option key={d.id} value={d.id}>
                              {d.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="revenue_target" className="label">Revenue target ($)</label>
                      <input
                        {...register('revenue_target')}
                        id="revenue_target"
                        type="text"
                        inputMode="decimal"
                        className="input"
                        placeholder="1000000"
                      />
                      {errors.revenue_target && (
                        <p className="mt-1 text-sm text-red-600">{errors.revenue_target.message}</p>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="cases_target" className="label">Cases target (optional)</label>
                        <input
                          {...register('cases_target')}
                          id="cases_target"
                          type="text"
                          inputMode="decimal"
                          className="input"
                          placeholder="25000"
                        />
                      </div>
                      <div>
                        <label htmlFor="new_operators_target" className="label">New operators (optional)</label>
                        <input
                          {...register('new_operators_target')}
                          id="new_operators_target"
                          type="number"
                          className="input"
                          placeholder="12"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="notes" className="label">Notes</label>
                      <textarea {...register('notes')} id="notes" rows={3} className="input" />
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
                  <button type="submit" disabled={isSaving} className="btn btn-primary">
                    {isSaving ? 'Saving…' : editing ? 'Save Changes' : 'Create Goal'}
                  </button>
                  <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Goals;
